import type { AuthStorage, AuthData } from '@dc-trans/data-layer';
import Cookies from 'js-cookie';

const AUTH_DATA_COOKIE_NAME = 'auth_data';

/**
 * Get the complete auth data from cookies
 */
export const getAuthData = (): AuthData | null => {
  try {
    const authDataString = Cookies.get(AUTH_DATA_COOKIE_NAME);
    return authDataString ? JSON.parse(authDataString) : null;
  } catch (error) {
    console.error('Error parsing auth data from cookies:', error);
    return null;
  }
};

/**
 * Set the complete auth data in cookies
 */
export const setAuthData = (authData: AuthData) => {
  try {
    Cookies.set(AUTH_DATA_COOKIE_NAME, JSON.stringify(authData), {
      path: '/',
      secure: false,
      sameSite: 'strict',
    });
  } catch (error) {
    console.error('Error storing auth data to cookies:', error);
  }
};

/**
 * Clear the auth data from cookies
 */
export const clearAuthData = () => {
  // Try to remove with the same options as when set
  Cookies.remove(AUTH_DATA_COOKIE_NAME, {
    path: '/',
    secure: false,
    sameSite: 'strict',
  });
};

/**
 * Web storage implementation for setup data-layer
 */
export const webStorage: AuthStorage = {
  getAuthData,
  setAuthData,
  clearAuthData,
};

export const isAuthenticated = () => {
  const authData = getAuthData();
  return !!authData?.access_token;
};

/**
 * Helper function to get the current user from stored auth data
 */
export const getCurrentUser = () => {
  const authData = getAuthData();
  return authData?.user || null;
};

/**
 * Helper function to get the access token from stored auth data
 */
export const getAccessToken = () => {
  const authData = getAuthData();
  return authData?.access_token || null;
};

/**
 * Helper function to get the refresh token from stored auth data
 */
export const getRefreshToken = () => {
  const authData = getAuthData();
  return authData?.refresh_token || null;
};
